import { useCallback, useMemo, useState } from "react";
import { updateChildConfig, updateDeviceInfo } from "../../util/children-helper";
import { Box, Typography } from "@mui/material";
import { ChildResponse } from "../../openapi";
import ModalWindow from "../ModalWindow";
import { useAlert } from "@/hooks/useAlert";
import { PhoneNumberInput } from "../PhoneNumberInput";
import { useCaregiverCountry } from "@/util/me-context";
import { useIsWatchChild } from "@/hooks";
import { useChildContext } from "@/context/child";
import { useTranslation } from "react-i18next";

interface ModalProps {
  isModalShown: boolean;
  setShowModal: (arg: boolean) => void;
  child: ChildResponse;
}

const ModalEnableGroupTexting = ({ isModalShown, setShowModal, child }: ModalProps) => {
  const caregiverCountryCode = useCaregiverCountry();
  const isWatchChild = useIsWatchChild();
  const { updateChild } = useChildContext();
  const { t } = useTranslation("common");
  // Currently our system designed for single device per child, so setting one phone number
  const hasAnyDevice = useMemo(() => {
    if (!child?.deviceInfos) return false;

    return child.deviceInfos.some((deviceInfo) => {
      if (isWatchChild) {
        return !!deviceInfo;
      } else {
        return !!deviceInfo.fcmDeviceToken;
      }
    });
  }, [child?.deviceInfos, isWatchChild]);

  const [phoneNumber, setPhoneNumber] = useState("");

  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const alert = useAlert();

  const updateChildPhone = useCallback(async () => {
    let phoneError = false;
    if (hasAnyDevice) {
      const data = await updateDeviceInfo(child.id, {
        ...child.deviceInfos[0],
        phoneNumber,
      });

      if (data) {
        const updatedChild = {
          ...child,
          deviceInfos: [...child.deviceInfos.filter((device) => device.deviceId !== data.deviceId), data],
        };
        updateChild(updatedChild);
      } else {
        phoneError = true;
        alert.error(t("failedUpdateChildPhone"));
      }
    }

    if (!phoneError) {
      const data = await updateChildConfig(child.id, {
        ...child.configuration,
        allowGroupMessaging: true,
      });
      if (data) {
        const updatedChild = { ...child, configuration: data };
        updateChild(updatedChild);
        setPhoneNumber("");
        setIsPhoneNumberValid(true);
        alert.success(t("groupMessagingEnabled"));
      } else {
        alert.error(t("failedEnableGroupMessaging"));
      }
    }
  }, [alert, child, hasAnyDevice, phoneNumber, updateChild, t]);

  const handleSave = useCallback(async (): Promise<void> => {
    try {
      await updateChildPhone();
      setShowModal(false);
    } catch (error) {
      alert.error(t("failedEnableGroupMessaging"));
    }
  }, [alert, setShowModal, updateChildPhone, t]);

  const getModalContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <p>{t("enterChildPhoneNumber", { childFirstName: child.firstName })}</p>
      <PhoneNumberInput
        defaultValue={hasAnyDevice && child.deviceInfos && child.deviceInfos[0].phoneNumber ? child.deviceInfos[0].phoneNumber : ""}
        label={t("phoneNumber")}
        countryCode={caregiverCountryCode}
        onChange={(value, { isValid }) => {
          setPhoneNumber(value);
          setIsPhoneNumberValid(isValid);
        }}
      />
      <Typography component={"p"} sx={{ fontSize: "0.8em", fontStyle: "italic", opacity: "0.8" }}>
        {t("unableToRetrievePhoneNumber")}
      </Typography>
    </Box>
  );

  return (
    <ModalWindow
      headerText={t("enableGroupTextingTitle")}
      showModal={isModalShown}
      setShowModal={setShowModal}
      renderBody={getModalContent}
      onActionButtonPress={handleSave}
      actionButtonType="primary"
      actionButtonText={t("save")}
      confirmDisabled={!isPhoneNumberValid}
    />
  );
};

export default ModalEnableGroupTexting;
